import { ActionButton } from '@emerald-works-nova/components'
import { useEvent, useEventsOnViewLoad } from '@emerald-works/react-event-bus-client'
import { Backdrop, CircularProgress, Grid, Tab, Tabs, useTheme } from '@mui/material'
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded'
import BubbleChartRoundedIcon from '@mui/icons-material/BubbleChartRounded'
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded'
import DomainIcon from '@mui/icons-material/Domain'
import VerticalSplitRoundedIcon from '@mui/icons-material/VerticalSplitRounded'
import VpnKeyRoundedIcon from '@mui/icons-material/VpnKeyRounded'
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded'
import classNames from 'classnames'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { generatePath, useNavigate, useParams } from 'react-router'
import { BannerStatic, Panel, SectionHeader, TabPanel } from '../../components'
import { AppSlice, TenantEditSlice, MSATenantConfigSlice, TemplateListSlice } from '../../reducers'
import { routes } from '../../routes'
import Branding from './branding-tab'
import DetailTab from './detail-tab'
import Features from './features-tab'
import SamlTab from './saml-tab'
import { useStyles } from './style'
import ProductsTab from './products-tab'
import { TenantFormContext } from './tenant-form-context'
import UsersTab from './users-tab'
import ReportsTab from './reports-tab'
import CMSTab from './cms-tab'
import { buildToolkitUrl } from '../../helpers/toolkit'
import { RegistrationDomainsTab } from './registration-domains-tab'
import { SummarizeRounded, Web } from '@mui/icons-material'
import { useArkMode } from '../../hooks/useArkMode'
import { TOOLKIT_TYPE, CONSTANTS } from '../../util/constants'

const SectionNames = {
  details: 'details',
  branding: 'branding',
  products: 'products',
  features: 'features',
  saml: 'saml',
  users: 'users',
  registrationDomains: 'registrationDomains',
  reports: 'reports',
  cms: 'cms'
}
const SectionMap = {
  [SectionNames.details]: {
    component: <DetailTab />,
    title: 'Details',
    icon: <AssignmentRoundedIcon />,
    next: SectionNames.branding,
    isVisible: () => true,
    isDisabled: () => false
  },
  [SectionNames.branding]: {
    component: <Branding />,
    title: 'Branding',
    icon: <BubbleChartRoundedIcon />,
    previous: SectionNames.details,
    next: SectionNames.products,
    isVisible: ({ isAdminMode }) => isAdminMode,
    isDisabled: () => false
  },
  [SectionNames.products]: {
    component: <ProductsTab />,
    title: 'Products',
    icon: <CategoryRoundedIcon />,
    previous: SectionNames.branding,
    next: SectionNames.features,
    isVisible: ({ isAdminMode }) => isAdminMode,
    isDisabled: () => false
  },
  [SectionNames.features]: {
    component: <Features />,
    title: 'Features',
    icon: <VerticalSplitRoundedIcon />,
    previous: SectionNames.products,
    next: SectionNames.saml,
    isVisible: ({ isAdminMode }) => isAdminMode,
    isDisabled: ({ isEdit, hasContentHub }) => !isEdit || !hasContentHub
  },
  [SectionNames.saml]: {
    component: <SamlTab />,
    title: 'SSO',
    icon: <VpnKeyRoundedIcon />,
    previous: SectionNames.features,
    isVisible: ({ isAdminMode }) => isAdminMode,
    isDisabled: ({ isEdit }) => !isEdit
  },
  [SectionNames.users]: {
    component: <UsersTab />,
    title: 'Users',
    icon: <PeopleAltRoundedIcon />,
    previous: SectionNames.saml,
    isVisible: ({ tenantKey }) => tenantKey !== CONSTANTS.DEFAULT_ADMIN_TENANT_KEY && tenantKey !== CONSTANTS.DEFAULT_CLUB_TENANT_KEY,
    isDisabled: ({ isEdit }) => !isEdit
  },
  [SectionNames.registrationDomains]: {
    component: <RegistrationDomainsTab />,
    title: 'Registration Domains',
    icon: <DomainIcon />,
    previous: SectionNames.users,
    isVisible: ({ tenantKey, isAdminMode }) => ![CONSTANTS.DEFAULT_CLUB_TENANT_KEY, CONSTANTS.DEFAULT_ADMIN_TENANT_KEY].includes(tenantKey) && isAdminMode,
    isDisabled: ({ isEdit }) => !isEdit
  },
  [SectionNames.reports]: {
    component: <ReportsTab />,
    title: 'Reports',
    icon: <SummarizeRounded />,
    isVisible: ({ isAdminMode }) => isAdminMode,
    isDisabled: ({ isEdit }) => !isEdit
  },
  [SectionNames.cms]: {
    component: <CMSTab />,
    title: 'CMS',
    icon: <Web />,
    isVisible: ({ tenantKey }) => tenantKey === CONSTANTS.DEFAULT_CLUB_TENANT_KEY,
    isDisabled: ({ isEdit }) => !isEdit
  }
}

const TenantEdit = () => {
  const classes = useStyles()
  const { spacing } = useTheme()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { isAdminMode, rootTenantPk } = useArkMode()
  const form = useForm({ mode: 'onChange' })
  const { getValues, watch, setValue, reset } = form
  const { id } = useParams()
  const isEdit = React.useMemo(() => !!id, [id])
  const envInfo = useSelector(AppSlice.selectors.selectEnvInfo)
  const tenant = useSelector(TenantEditSlice.selectors.tenant)

  const [tabValue, setTabValue] = React.useState(Object.entries(SectionMap)[0][0])
  const handleTabChange = React.useCallback((_, newValue) => setTabValue(newValue), [setTabValue])

  const [isLoading, setIsLoading] = React.useState(false)
  const [loadingMsg, setLoadingMsg] = React.useState()
  const [hasContentHub, setHasContentHub] = React.useState(false)

  const [tenantInsert, tenantUpdate, createUser, getTenant, getMSATenantConfig, getParentTemplatesList] = useEvent([
    { eventName: 'tenantInsert', shouldTimeout: false },
    { eventName: 'tenantUpdate' },
    { eventName: 'createTempManagerUser' },
    TenantEditSlice.eventBus.getTenant,
    MSATenantConfigSlice.eventBus.getMSATenantConfig,
    TemplateListSlice.eventBus.getParentTemplatesList
  ])

  useEventsOnViewLoad(() => {
    if (!isEdit) {
      getParentTemplatesList.trigger({ populate: true })
    }

    if (isEdit) {
      getMSATenantConfig.trigger({ tenantId: id, retrieveGlobalConfig: true })
    }
    if (!id || !rootTenantPk) return
    setLoadingMsg('Loading toolkit data...')
    setIsLoading(true)

    if (!isAdminMode && id !== rootTenantPk) {
      throw new Error('Not authorized to access this toolkit')
    }
    getTenant.trigger({ pk: id, withTemplate: true })
  }, [getTenant, rootTenantPk, id])

  useEventsOnViewLoad(() => {
    tenantInsert.registerAdhocOnSuccessListener(({ payload: { tenant } }) => {
      const {
        pk,
        userPoolId,
        cognitoSettings: { identityPoolId }
      } = tenant
      createUser.trigger({ tenantId: pk, userPoolId, identityPoolId })

      BannerStatic.show({ label: 'Toolkit saved successfully' })
      setIsLoading(false)

      navigate(generatePath(routes.tenantDetails.path, { id: tenant.pk }), { state: { tenant }, replace: true })
    })
    tenantInsert.registerAdhocOnErrorListener((...args) => {
      console.log('tenantInsert.registerAdhocOnErrorListener', args)
      BannerStatic.show({ label: 'There was an error creating the Client Toolkit', isError: true })
    })

    tenantUpdate.registerAdhocOnSuccessListener(() => {
      BannerStatic.show({ label: 'Toolkit updated successfully' })
      setIsLoading(false)
    })
  }, [tenantInsert, tenantUpdate, createUser])

  React.useEffect(() => {
    if (!tenant) return
    reset(tenant)

    setValue('tenantId', tenant.pk)

    // Keep tenantUrl after form reset
    const tenantKey = tenant.tenantKey
    if (tenantKey && envInfo) {
      setValue(
        'tenantUrl',
        buildToolkitUrl(tenantKey, envInfo.stage, envInfo.tld)
      )
    }

    const hasContentHubField = Object.hasOwn(tenant?.features, 'contentHub')
    const contentHub = tenant?.features?.contentHub
    setHasContentHub(hasContentHubField ? contentHub : true)

    setIsLoading(false)
  }, [tenant, reset, setValue, envInfo])

  // reset redux store on leave
  React.useEffect(() => {
    return () => dispatch(TenantEditSlice.actions.reset())
  }, [dispatch])

  const handleCreateTenant = React.useCallback(() => {
    if (tabValue === SectionNames.products && !isEdit) {
      const tenantData = {
        ...getValues(),
        tenantUrl: undefined,
        createdAt: undefined,
        updatedAt: undefined
      }

      if (!tenantData.features?.contentHub) {
        delete tenantData.baseTemplate
      }

      if (tenantData.meteringLimit) {
        tenantData.meteringLimit = Number(tenantData.meteringLimit)
      }

      if (tenantData.meteringResetCycle) {
        tenantData.meteringResetCycle = Number(tenantData.meteringResetCycle)
      }

      tenantData.maxNumberOfUsers = tenantData.maxNumberOfUsers ? Number(tenantData.maxNumberOfUsers) : null

      if (tenantData.toolkitType !== TOOLKIT_TYPE.club) {
        tenantData.meteringEnabled = false
        tenantData.stubbedContentEnabled = false
      }

      if (tenantData?.templateName) delete tenantData.templateName

      tenantInsert.trigger(tenantData)
      setLoadingMsg('Saving toolkit...')
      setIsLoading(true)
      return
    }
    setTabValue(SectionMap[tabValue].next)
  }, [tenantInsert, isEdit, getValues, tabValue, setTabValue])

  const handleNextClick = React.useCallback(
    () => setTabValue(SectionMap[tabValue].next),
    [tabValue]
  )
  const handlePreviousClick = React.useCallback(
    () => setTabValue(SectionMap[tabValue].previous),
    [tabValue]
  )

  const onUpdateTenant = React.useCallback(() => {
    const tenantData = {
      ...getValues(),
      tenantUrl: undefined,
      createdAt: undefined,
      updatedAt: undefined
    }

    if (tenantData.meteringLimit) {
      tenantData.meteringLimit = Number(tenantData.meteringLimit)
    }

    if (tenantData.meteringResetCycle) {
      tenantData.meteringResetCycle = Number(tenantData.meteringResetCycle)
    }

    tenantData.maxNumberOfUsers = tenantData.maxNumberOfUsers ? Number(tenantData.maxNumberOfUsers) : null

    if (tenantData.toolkitType !== TOOLKIT_TYPE.club) {
      tenantData.meteringEnabled = false
      tenantData.stubbedContentEnabled = false
    }

    if (tenantData?.templateName) delete tenantData.templateName
    tenantUpdate.trigger(tenantData)
    setLoadingMsg('Updating toolkit...')
    setIsLoading(true)
  }, [tenantUpdate, getValues])

  const handleCancel = React.useCallback(() => navigate(routes.tenantList.path), [navigate])

  const tenantName = watch('name')

  if ((id && !tenant) || isLoading) {
    return (
      <Backdrop
        id='backdrop-loading'
        className={classes.backdrop}
        open={isLoading}
      >
        <Grid
          container
          direction='column'
          className={classes.loadingCtn}
          justifyContent='center'
          alignItems='center'
        >
          <Grid item>
            <CircularProgress size={100} />
          </Grid>
          <Grid item className={classes.loadingMsg}>
            <strong>{loadingMsg}</strong>
          </Grid>
        </Grid>
      </Backdrop>
    )
  }

  return (
    <TenantFormContext.Provider
      value={{
        form,
        tenant,
        isEdit,
        mainClasses: classes,
        setIsLoading,
        setLoadingMsg,
        onUpdateTenant,
        envInfo,
        handleNextClick,
        handlePreviousClick,
        handleCreateTenant,
        getValues
      }}
    >
      <Grid container style={{ marginBottom: spacing(3) }}>
        <Grid item xs={9} data-test='title'>
          <SectionHeader
            title={tenantName || 'New Toolkit'}
            location='Client Toolkit /'
          />
        </Grid>
        <Grid item xs={3} className={classes.controlBtnContainer}>
          {isAdminMode && (
            <ActionButton
              variant='secondary'
              dataTest='backtolist-button'
              onClick={handleCancel}
            >
              {' '}
              Back to list
            </ActionButton>
          )}
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        {/* Tenant tabs */}
        <Grid item xs={12}>
          <Panel padding={0}>
            <Grid container>
              <Grid item xs={12}>
                <Tabs
                  indicatorColor='primary'
                  textColor='primary'
                  value={tabValue}
                  onChange={handleTabChange}
                  aria-label='Tenant Tabs'
                  classes={{
                    root: classes.tabs
                  }}
                >
                  {Object.entries(SectionMap)
                    // SSO tab is only visible when editing <- is it though?
                    .filter(([key]) => SectionMap[key].isVisible({ tenantKey: tenant?.tenantKey, isEdit, isAdminMode }))
                    .map(([key, section]) => (
                      <Tab
                        id={`tab-${key}`}
                        aria-controls={`tabpanel-${key}`}
                        key={key}
                        value={key}
                        label={section.title}
                        icon={section.icon}
                        data-test={`tab-${key}`}
                        classes={{
                          wrapper: classes.tabWrapper,
                          root: classNames(
                            {
                              [classes.tabDisabled]: SectionMap[key].isDisabled(
                                { isEdit, hasContentHub }
                              )
                            },
                            { [classes.clickDisabled]: !isEdit }
                          )
                        }}
                      />
                    ))}
                </Tabs>
              </Grid>
            </Grid>
            {/* Tabs content */}
            {Object.entries(SectionMap).map(([key]) => (
              <TabPanel key={key} value={tabValue} index={key}>
                {SectionMap[key].component}
              </TabPanel>
            ))}
          </Panel>
        </Grid>
      </Grid>
    </TenantFormContext.Provider>
  )
}

export default TenantEdit
